import React, { useState, useCallback } from 'react';
import { useMutation } from 'react-query';
import { validateEmail } from 'helpers';
import { toast } from 'utils/toast';
import { AUTH_MESSAGES } from 'locales/en-US';
import { api } from 'api/api';
import { logEvent } from 'firebase/analytics';
import { getAnalyticsInstance } from 'firebase.js';
import { useTeamColors } from 'hooks';

import CustomInput from 'components/CustomInput';
import Button from 'components/Button';
import Link from 'components/Link';
import { BackIcon } from 'assets/icons';

import { LinksContainer, ArrowWrapper } from '../style.js';

const RestorePasswordForm = () => {
  const analytics = getAnalyticsInstance();
  const { mainColor } = useTeamColors();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);

  const sendRestoreEmail = useMutation((data) => api.user.resetPassword(data), {
    onError: () => {
      toast.error(AUTH_MESSAGES.passwordResetError);

      logEvent(analytics, 'select_content', {
        content_type: 'error',
        content_id: `An error occurs during restoring password for user with email ${email}. Site url: ${window.location.href}`,
      });
    },
    onSuccess: () => {
      toast.success(AUTH_MESSAGES.passwordResetSent(email));
      setEmail('');
    },
  });

  const emailInputHandler = useCallback(
    (inputValue) => {
      setEmail(inputValue);
      if (inputValue !== '' && !validateEmail(email)) {
        setEmailError(true);
        return;
      }
      setEmailError(false);
    },
    [email]
  );

  const emailErrorHandler = useCallback(() => {
    if (email !== '' && !validateEmail(email)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
  }, [email]);

  const restorePasswordHandler = useCallback(() => {
    if (email && !emailError) {
      sendRestoreEmail.mutate({ email });
    }
  }, [email, emailError, sendRestoreEmail]);

  const isButtonDisabled = !email || emailError;

  return (
    <>
      <CustomInput
        isError={emailError}
        onBlur={emailErrorHandler}
        onChange={emailInputHandler}
        placeholder="Email"
        type="email"
        value={email}
      />
      <LinksContainer>
        <Link route="/" title="Return">
          <ArrowWrapper>
            <BackIcon color={mainColor} />
          </ArrowWrapper>
        </Link>
      </LinksContainer>
      <Button
        disabled={isButtonDisabled || sendRestoreEmail.isLoading}
        onClick={restorePasswordHandler}
        title="Reset password"
      />
    </>
  );
};

export default RestorePasswordForm;
